import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  Check,
  Download,
  LockKeyhole,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import {
  DemoDisclaimer,
  LogItDemo,
  RhythmDemo,
  SayItNaturallyDemo,
} from "./components/FauxChatDemo";
import { ChatGptCta } from "./components/ChatGptCta";
import { AppStoreCta } from "./components/AppStoreCta";
import heppaMain from "./assets/mascots/heppa-main.svg";
import heppaGreen from "./assets/mascots/heppa-green.svg";
import heppaBlue from "./assets/mascots/heppa-blue.svg";
import heppaYellow from "./assets/mascots/heppa-yellow.svg";
import heppaOrange from "./assets/mascots/heppa-orange.svg";
import hepapalMenuCard from "./assets/product/hepapal-menu-card.png";
import frequencyCalendar from "./assets/product/heppal-ios-menu.png";
import yearReport from "./assets/product/year-report-phone.png";
import reminderScreen from "./assets/product/hepapal-reminder.jpg";
import dataExportScreen from "./assets/product/hepapal-data-export.jpg";
import passcodeScreen from "./assets/product/hepapal-passcode.jpg";
import featureArrow from "./assets/product/feature-arrow.svg";
import heroLogoGroup from "./assets/brand/4-logo-tras.svg";

const privacyItems: Array<{ icon: LucideIcon; title: string; text: string }> = [
  { icon: LockKeyhole, title: "Passcode protection", text: "Add a private layer around your personal view." },
  { icon: Download, title: "Export your data", text: "Take a copy of your records whenever you need it." },
  { icon: Trash2, title: "Delete your data", text: "Remove your records with a clear, direct action." },
];

const healthNotes = [
  {
    index: "01",
    title: "A drink is more than the glass",
    text: "Beer, wine, and spirits can hold very different amounts of alcohol. Glass size alone does not tell the whole story; a U.S. standard drink is defined by its pure alcohol content.",
    source: "NIAAA · Standard drinks",
    href: "https://www.niaaa.nih.gov/alcohols-effects-health/what-standard-drink",
  },
  {
    index: "02",
    title: "A calendar can make a pattern visible",
    text: "NIAAA's Rethinking Drinking offers a four-week tracker and notes that recording drinks can help people notice patterns when they are considering a change.",
    source: "NIAAA · Alcohol consumption tracker",
    href: "https://rethinkingdrinking.niaaa.nih.gov/tools/worksheets-more/alcohol-consumption-tracker",
  },
  {
    index: "03",
    title: "Medicine labels deserve a second look",
    text: "Some prescription medicines, over-the-counter products, and herbal remedies can interact with alcohol. Read the label and ask a pharmacist or clinician when you are unsure.",
    source: "NIAAA · Alcohol and medicines",
    href: "https://www.niaaa.nih.gov/publications/brochures-and-fact-sheets/harmful-interactions-mixing-alcohol-with-medicines",
  },
  {
    index: "04",
    title: "Falling asleep is not the whole night",
    text: "Alcohol may make sleep arrive sooner, while still fragmenting sleep and contributing to earlier waking. That is useful context, not a diagnosis of how any one person feels.",
    source: "NIAAA · Hangovers and sleep",
    href: "https://www.niaaa.nih.gov/publications/brochures-and-fact-sheets/hangovers",
  },
];

const productDetails = [
  {
    id: "reminder",
    label: "01 · CHECK IN",
    title: "Gentle reminders",
    text: "Choose whether Heppa checks in tomorrow, with a time that fits your evening.",
    image: reminderScreen,
    alt: "HepaPal reminder prompt asking whether Heppa should check in tomorrow",
  },
  {
    id: "passcode",
    label: "02 · KEEP PRIVATE",
    title: "Passcode protection",
    text: "Add a four-digit passcode before opening your private HepaPal view.",
    image: passcodeScreen,
    alt: "HepaPal screen for setting a four-digit passcode",
  },
  {
    id: "export",
    label: "03 · TAKE A COPY",
    title: "Exportable history",
    text: "Keep a dated spreadsheet of your records and notes for your own review.",
    image: dataExportScreen,
    alt: "A spreadsheet exported from HepaPal with dates, statuses, and summary counts",
  },
];

function SiteHeader() {
  return (
    <header className="site-header">
      <div className="page-width header-inner">
        <a className="brand" href="#top" aria-label="HepaPal home">
          <img src={heppaBlue} alt="" />
          <span>HepaPal</span>
          <small>WITH HEPPA</small>
        </a>
        <nav className="site-nav" aria-label="Main navigation">
          <a href="#how">How it works</a>
          <a href="#privacy">Privacy</a>
          <a href="#notes">Gentle notes</a>
        </nav>
        <ChatGptCta className="header-cta" label="Try HepaPal" />
      </div>
    </header>
  );
}

function SectionIntro({ label, title, text }: { label: string; title: string; text: string }) {
  return (
    <div className="section-intro">
      <span className="eyebrow">{label}</span>
      <h2>{title}</h2>
      <p>{text}</p>
    </div>
  );
}

function Hero() {
  return (
    <section className="hero" id="top">
      <div className="hero-field" aria-hidden="true" />
      <div className="page-width hero-inner">
        <div className="hero-copy">
          <span className="eyebrow">A SOFTER DAILY CHECK-IN</span>
          <h1>HepaPal</h1>
          <p className="hero-lead">A gentle drinking habit tracker inside ChatGPT.</p>
          <p className="hero-support">No shame. No pressure. Just a softer way to notice.</p>
          <div className="hero-actions">
            <ChatGptCta
              className="button primary-button"
              label="Try HepaPal in ChatGPT"
            />
            <AppStoreCta className="button secondary-button" />
          </div>
          <p className="privacy-line"><LockKeyhole aria-hidden="true" /> Private by design. Your records stay yours.</p>
          <a className="next-section-link" href="#how">See how it works <ArrowRight aria-hidden="true" /></a>
        </div>

        <div className="hero-scene">
          <img
            className="hero-logo-group"
            src={heroLogoGroup}
            alt="Heppa with a calendar beside a smiling Heppa and illustrated AI chat and App Store symbols"
          />
          <p className="hero-art-note"><ShieldCheck aria-hidden="true" /> A product-flow illustration. No official chat interface or endorsement implied.</p>
        </div>
      </div>
    </section>
  );
}

function DemoSequence() {
  return (
    <section className="demo-sequence-section" id="how">
      <div className="page-width">
        <SectionIntro
          label="HOW IT WORKS"
          title="Three small moments, shown as they happen."
          text="Each scene uses a generic, code-built AI chat surface. Every step now has its own gentle motion checkpoint."
        />

        <div className="demo-sequence">
          <article className="demo-story demo-story-one">
            <div className="demo-frame-wrap">
              <header className="demo-corner-label">
                <span>01</span>
                <h3>Say it naturally</h3>
              </header>
              <SayItNaturallyDemo />
            </div>
            <p className="demo-story-description">Call Heppa in your own words. The input types, the cursor moves, and the message sends.</p>
            <DemoDisclaimer />
          </article>

          <article className="demo-story demo-story-two">
            <div className="demo-frame-wrap">
              <header className="demo-corner-label">
                <span>02</span>
                <h3>Let Heppa log it</h3>
              </header>
              <LogItDemo initialMascot={heppaGreen} selectedMascot={heppaYellow} />
            </div>
            <p className="demo-story-description">The cursor chooses the warm yellow option, then HepaPal confirms the log and gently refreshes the tracking summary.</p>
            <DemoDisclaimer />
          </article>

          <article className="demo-story demo-story-three">
            <div className="demo-frame-wrap">
              <header className="demo-corner-label">
                <span>03</span>
                <h3>See your rhythm</h3>
              </header>
              <RhythmDemo mascot={heppaBlue} />
            </div>
            <p className="demo-story-description">Ask about the week, then let a calm seven-day rhythm come gently into view.</p>
            <DemoDisclaimer />
          </article>
        </div>
      </div>
    </section>
  );
}

function ProductViewsSection() {
  return (
    <section className="product-section" id="product">
      <div className="page-width">
        <div className="product-inner">
          <SectionIntro
            label="REAL PRODUCT VIEWS"
            title="From today's choice to a longer rhythm."
            text="These are product screens, not decorative mockups: a daily card, a working calendar, and a longer trend view."
          />
          <div className="product-gallery">
            <figure className="product-figure product-figure-chat">
              <div className="chat-card-frame">
                <img src={hepapalMenuCard} alt="Real HepaPal daily check-in card showing four simple logging choices" />
              </div>
              <figcaption><strong>HepaPal daily card</strong><span>The real card shown inside the product flow.</span></figcaption>
            </figure>
            <figure className="product-figure product-figure-calendar">
              <div className="device-shot-frame">
                <img src={frequencyCalendar} alt="HepaPal iPhone screen with four logging choices and a monthly calendar" />
              </div>
              <figcaption><strong>Daily logging + calendar</strong><span>Quick choices, past-day edits, and a visible month.</span></figcaption>
            </figure>
            <figure className="product-figure product-figure-report">
              <div className="device-shot-frame">
                <img src={yearReport} alt="HepaPal yearly trend report with a bar chart and count summary" />
              </div>
              <figcaption><strong>Yearly trend report</strong><span>Counts and patterns shown without a health score.</span></figcaption>
            </figure>
          </div>
        </div>

        <div className="product-detail-band">
          <header className="product-detail-heading">
            <span className="eyebrow">MORE YOU CAN DO</span>
            <h3>Small controls, kept close.</h3>
          </header>
          <div className="product-detail-grid">
            {productDetails.map((detail) => (
              <article className={`product-detail product-detail-${detail.id}`} key={detail.id}>
                <figure className="product-detail-visual">
                  <img src={detail.image} alt={detail.alt} />
                </figure>
                <img className="product-detail-arrow" src={featureArrow} alt="" aria-hidden="true" />
                <div className="product-detail-copy">
                  <span className="tiny-label">{detail.label}</span>
                  <h4>{detail.title}</h4>
                  <p>{detail.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyPeopleUseIt() {
  return (
    <section className="why-section">
      <div className="page-width why-inner">
        <div>
          <span className="eyebrow">WHY PEOPLE USE IT</span>
          <h2>Different reasons. The same gentle space.</h2>
        </div>
        <div className="why-list">
          <p><Check aria-hidden="true" /><span><strong>For simple tracking</strong>Keep the day without building a spreadsheet.</span></p>
          <p><Check aria-hidden="true" /><span><strong>For drinking less</strong>Notice choices without demanding perfection.</span></p>
          <p><Check aria-hidden="true" /><span><strong>For understanding patterns</strong>Look back with context, not a score.</span></p>
        </div>
      </div>
    </section>
  );
}

function PrivacySection() {
  return (
    <section className="privacy-section" id="privacy">
      <div className="page-width">
        <SectionIntro
          label="PRIVACY"
          title="Personal notes deserve personal control."
          text="HepaPal is designed around clear choices for keeping, taking, or removing your data."
        />
        <div className="privacy-grid">
          {privacyItems.map(({ icon: Icon, title, text }) => (
            <article className="privacy-item" key={title}>
              <Icon aria-hidden="true" />
              <div><h3>{title}</h3><p>{text}</p></div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function HealthNewspaper() {
  return (
    <section className="health-section" id="notes">
      <div className="page-width">
        <div className="health-paper">
          <header className="health-masthead">
            <img className="news-heppa news-heppa-yellow" src={heppaYellow} alt="Yellow Heppa smiling" />
            <div>
              <span className="tiny-label">HEPAPAL HEALTH NOTES · EDITION 01</span>
              <h2>The Gentle Health Ledger</h2>
              <p>Four sourced notes to keep nearby. Calm context, never a diagnosis.</p>
            </div>
            <img className="news-heppa news-heppa-orange" src={heppaOrange} alt="Orange Heppa smiling" />
          </header>

          <div className="health-dateline">
            <span>READ SLOWLY</span>
            <span>SOURCES: NIAAA</span>
            <span>HABITS, NOT HEALTH SCORES</span>
          </div>

          <div className="news-grid">
            {healthNotes.map((note) => (
              <article className="news-story" key={note.index}>
                <span className="news-index">{note.index}</span>
                <h3>{note.title}</h3>
                <p>{note.text}</p>
                <a href={note.href} target="_blank" rel="noreferrer">
                  {note.source} <ArrowRight aria-hidden="true" />
                </a>
              </article>
            ))}
          </div>

          <footer className="paper-boundary">
            <ShieldCheck aria-hidden="true" />
            <p><strong>A habit tracker, not a medical tool.</strong> No diagnosis. No treatment. For health concerns, speak with a qualified professional.</p>
          </footer>
        </div>
      </div>
    </section>
  );
}

function BoundarySection() {
  return (
    <section className="boundary-section" aria-label="HepaPal health boundary">
      <div className="page-width boundary-inner">
        <span className="boundary-icon"><ShieldCheck aria-hidden="true" /></span>
        <div>
          <span className="eyebrow">A CLEAR BOUNDARY</span>
          <h2>Notice a habit. Keep medical decisions elsewhere.</h2>
          <p>HepaPal records what you tell it. It does not diagnose, treat, calculate health scores, or assess liver function.</p>
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="final-cta">
      <div className="page-width final-cta-inner">
        <img className="final-cta-mascot" src={heppaMain} alt="Heppa smiling" />
        <div>
          <span className="eyebrow">HEPPA IS READY WHEN YOU ARE</span>
          <h2>Start with today. Nothing more.</h2>
          <p>A small check-in can simply be a small check-in.</p>
        </div>
        <div className="final-actions">
          <ChatGptCta className="button primary-button" label="Try HepaPal" />
          <AppStoreCta variant="badge" />
        </div>
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="page-width footer-inner">
        <p>HepaPal <span>WITH HEPPA</span></p>
        <div>
          <a href="https://www.hepapal.net/privacy">Privacy</a>
          <a href="https://www.hepapal.net/terms">Terms</a>
          <a href="mailto:support@hepapal.net" aria-label="Email HepaPal support at support@hepapal.net">Contact</a>
        </div>
        <small className="platform-credit">Apple and the Apple logo are trademarks of Apple Inc. App Store is a service mark of Apple Inc. ChatGPT is a trademark of OpenAI.</small>
        <small className="creator-credit">Designed and created by CiCi</small>
      </div>
    </footer>
  );
}

function DemoOneReview() {
  return (
    <main className="demo-review-page" id="main-content">
      <div className="review-shell">
        <header className="review-heading">
          <div>
            <span className="eyebrow">CHECKPOINT 01 · DEMO 1 OF 3</span>
            <h1>Say it naturally</h1>
            <p>Review the cursor path, click feedback, typing pace, send action, and reply reveal here without the rest of the homepage.</p>
          </div>
          <a href="/">View full homepage <ArrowRight aria-hidden="true" /></a>
        </header>
        <div className="demo-frame-wrap demo-story-one">
          <header className="demo-corner-label">
            <span>01</span>
            <h3>Say it naturally</h3>
          </header>
          <SayItNaturallyDemo compact />
        </div>
        <DemoDisclaimer />
        <p className="review-note">Demo 2 and Demo 3 each have their own review checkpoint.</p>
      </div>
    </main>
  );
}

function DemoTwoReview() {
  return (
    <main className="demo-review-page" id="main-content">
      <div className="review-shell">
        <header className="review-heading">
          <div>
            <span className="eyebrow">CHECKPOINT 02 · DEMO 2 OF 3</span>
            <h1>Let Heppa log it</h1>
            <p>Review the cursor target, press feedback, warm yellow selection, Logged confirmation, Heppa color change, and delayed tracking update.</p>
          </div>
          <a href="/">View full homepage <ArrowRight aria-hidden="true" /></a>
        </header>
        <div className="demo-frame-wrap demo-story-two">
          <header className="demo-corner-label">
            <span>02</span>
            <h3>Let Heppa log it</h3>
          </header>
          <LogItDemo initialMascot={heppaGreen} selectedMascot={heppaYellow} />
        </div>
        <DemoDisclaimer />
        <p className="review-note">Demo 3 now has its own weekly-rhythm review checkpoint.</p>
      </div>
    </main>
  );
}

function DemoThreeReview() {
  return (
    <main className="demo-review-page" id="main-content">
      <div className="review-shell">
        <header className="review-heading">
          <div>
            <span className="eyebrow">CHECKPOINT 03 · DEMO 3 OF 3</span>
            <h1>See your rhythm</h1>
            <p>Review the question, Heppa's reply, weekly card reveal, six colored days through the eighteenth, and four matching status counts.</p>
          </div>
          <a href="/">View full homepage <ArrowRight aria-hidden="true" /></a>
        </header>
        <div className="demo-frame-wrap demo-story-three">
          <header className="demo-corner-label">
            <span>03</span>
            <h3>See your rhythm</h3>
          </header>
          <RhythmDemo mascot={heppaBlue} />
        </div>
        <DemoDisclaimer />
        <p className="review-note">This is the final faux-chat animation checkpoint. The remaining homepage sections are unchanged.</p>
      </div>
    </main>
  );
}

export function HomePage() {
  const demo = new URLSearchParams(window.location.search).get("demo");

  if (demo === "1") {
    return <DemoOneReview />;
  }

  if (demo === "2") {
    return <DemoTwoReview />;
  }

  if (demo === "3") {
    return <DemoThreeReview />;
  }

  return (
    <>
      <a className="skip-link" href="#main-content">Skip to content</a>
      <SiteHeader />
      <main id="main-content">
        <Hero />
        <DemoSequence />
        <WhyPeopleUseIt />
        <ProductViewsSection />
        <PrivacySection />
        <HealthNewspaper />
        <BoundarySection />
        <FinalCta />
      </main>
      <SiteFooter />
    </>
  );
}
